/**
 * Bulk entry. A register of sources, areas or equipment is pasted straight out of a spreadsheet;
 * nothing changes until the engineer has read what it would do, row by row.
 */
import { useMemo, useState } from 'react';
import b from './BulkPaste.module.css';
import { readTable, summarise, type BulkKind } from '../io/bulk';
import { newId } from '../state/db';
import { useStore } from '../state/store';
import { Button } from '../ui/kit';

const KINDS: Array<{ value: BulkKind | 'auto'; label: string }> = [
  { value: 'auto', label: 'Work it out' },
  { value: 'sources', label: 'Sources of release' },
  { value: 'areas', label: 'Areas' },
  { value: 'equipment', label: 'Equipment' },
];

const PLACEHOLDER = 'Tag\tDescription\tArea\tSubstance\tGrade\tX\tY\tZ\nSR-410\tPump seal P-410\tPump house\tToluene\tSecondary\t2.0\t1.5\t0.6';

export function BulkPaste() {
  const open = useStore((s) => s.bulkOpen);
  const setBulk = useStore((s) => s.setBulk);
  const doc = useStore((s) => s.doc);
  const [text, setText] = useState('');
  const [kind, setKind] = useState<BulkKind | 'auto'>('auto');

  const result = useMemo(
    () => (doc && text.trim() ? readTable(text, doc, newId, kind === 'auto' ? undefined : kind) : undefined),
    [doc, text, kind],
  );

  if (!open || !doc) return null;

  const close = () => { setText(''); setKind('auto'); setBulk(false); };
  const usable = result ? result.rows.filter((r) => r.what !== 'skip').length : 0;

  const apply = () => {
    if (!result || !usable) return;
    const { sources, areas, equipment } = result;
    const label = `Paste ${summarise(result)}`;
    useStore.getState().edit(label, (d) => {
      for (const a of areas) {
        const i = d.areas.findIndex((x) => x.id === a.id);
        if (i >= 0) d.areas[i] = a; else d.areas.push(a);
      }
      for (const s of sources) {
        const i = d.sources.findIndex((x) => x.id === s.id);
        if (i >= 0) d.sources[i] = s; else d.sources.push(s);
      }
      for (const e of equipment) {
        const i = d.equipment.findIndex((x) => x.id === e.id);
        if (i >= 0) d.equipment[i] = e; else d.equipment.push(e);
      }
    });
    useStore.getState().notify(`Pasted: ${summarise(result)}.`);
    close();
  };

  return (
    <div className={b.backdrop} onPointerDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className={b.panel} role="dialog" aria-modal="true" aria-label="Paste rows from a spreadsheet" onKeyDown={(e) => { if (e.key === 'Escape') close(); }}>
        <div className={b.head}>
          <h2>Paste rows</h2>
          <p>Copy a block of cells from a spreadsheet, including its header row, and paste it here. Nothing changes until you press the button below.</p>
        </div>

        <div className={b.body}>
          <textarea
            className={b.paste}
            value={text}
            autoFocus
            spellCheck={false}
            placeholder={PLACEHOLDER}
            aria-label="Pasted rows"
            onChange={(e) => setText(e.target.value)}
          />

          <div className={b.row}>
            <label htmlFor="bulk-kind">These rows are</label>
            <select id="bulk-kind" value={kind} onChange={(e) => setKind(e.target.value as BulkKind | 'auto')}>
              {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
            {result && <span className={b.count}>{summarise(result)}</span>}
          </div>

          {result?.problems.map((p) => <p key={p} className={b.problem}>{p}</p>)}

          {result && result.rows.length > 0 && (
            <>
              <p className={b.cols}>
                <b>Read:</b> {result.recognised.join(', ') || 'nothing'}
                {result.ignored.length > 0 && <> · <b>ignored:</b> {result.ignored.join(', ')}</>}
              </p>
              <table className={b.preview}>
                <thead>
                  <tr><th scope="col">Line</th><th scope="col">Tag or name</th><th scope="col">What happens</th><th scope="col">Notes</th></tr>
                </thead>
                <tbody>
                  {result.rows.map((r) => (
                    <tr key={r.line}>
                      <td>{r.line}</td>
                      <td className={b.key}>{r.key || '—'}</td>
                      <td className={b.what} data-what={r.what}>
                        {r.what === 'add' ? 'Added' : r.what === 'update' ? 'Updated' : 'Skipped'}
                      </td>
                      <td className={b.issue} data-skip={r.what === 'skip'}>{r.issues.join(' ')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>

        <div className={b.foot}>
          <span className={b.spacer} />
          <Button onClick={close}>Cancel</Button>
          <Button variant="primary" disabled={!usable} onClick={apply}>
            {usable ? `Apply ${usable} row${usable === 1 ? '' : 's'}` : 'Apply'}
          </Button>
        </div>
      </div>
    </div>
  );
}
